import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { startRLTraining, getTrainingStatus, listTrainingSessions, startEvolutionaryTraining, getEvolutionaryStatus, listEvolutionaryExperiments, deployCombinatorialBest, deploySelectedModels, storeSelectedModels, deleteEvolutionaryExperiment, stopEvolutionaryExperiment, getAvailableSymbols, listTopModelsSummary, getIndicatorDefaults } from '../../api/rlApi';
import './RLTraining.css';

function RLTraining() {
  const [trainingConfig, setTrainingConfig] = useState({
    algorithm: 'PPO',
    total_timesteps: 50000,
    learning_rate: 0.0008,
    batch_size: 96,
    gamma: 0.99,
    verbose: 1
  });

  const [evolutionaryConfig, setEvolutionaryConfig] = useState({
    population_size: 16,
    generations: 5,
    elitism_rate: 0.25,
    mutation_rate: 0.3,
    total_timesteps: 50000,
    symbols: ["BTC/USDT", "ETH/USDT", "SOL/USDT", "TRX/USDT", "XRP/USDT"],
    initial_balance: 10000.0,
    transaction_fee: 0.001,
    max_position_size: 0.05,
    timeframe: "1h",
    indicators: {
      // Current indicators
      MFI: true,
      VWAP: true,
      GM_Confluence: true,

      // Breakout indicators
      Donchian_Channels: true,
      Keltner_Channels: true,
      Price_Channels: true,
      Volume_Breakouts: true,

      // Timing model indicators
      Fibonacci_Extensions: true,
      Gann_Angles: true,
      Andrews_Pitchfork: true,
      Wolfe_Waves: true,

      // Momentum timing indicators
      Williams_Alligator: true,
      Fractal_Dimension: true,
      Hurst_Exponent: true,

      // Volume timing indicators
      Volume_Profile: true,
      Order_Flow: true,
      POC: true,
      CVD: true,

      // Market Structure Layer
      Swing_High_Low: false,
      Order_Blocks: false,
      Fair_Value_Gaps: false,
      Liquidity_Pools: false,
      Market_Structure: false,
    },
    combinatorial_testing: false
  });

  const [activeSessions, setActiveSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetails, setSessionDetails] = useState(null);
  const [isTraining, setIsTraining] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Evolutionary training state
  const [activeExperiments, setActiveExperiments] = useState([]);
  const [selectedExperiment, setSelectedExperiment] = useState(null);
  const [experimentDetails, setExperimentDetails] = useState(null);
  const [isEvolutionaryTraining, setIsEvolutionaryTraining] = useState(false);
  const [evolutionaryLoading, setEvolutionaryLoading] = useState(false);
  const [evolutionaryError, setEvolutionaryError] = useState(null);
  const [deletingExperiment, setDeletingExperiment] = useState(false);
  const [stoppingExperiment, setStoppingExperiment] = useState(false);

  // Learning rate testing state
  const [learningRateExperiments, setLearningRateExperiments] = useState([]);
  const [isLearningRateTesting, setIsLearningRateTesting] = useState(false);
  const [learningRateResults, setLearningRateResults] = useState(null);
  const [sessionsUpdatedAt, setSessionsUpdatedAt] = useState(null);
  const [experimentsUpdatedAt, setExperimentsUpdatedAt] = useState(null);

  // Model selection state for combinatorial results
  const [selectedCombinations, setSelectedCombinations] = useState([]);
  const [isDeployingSelected, setIsDeployingSelected] = useState(false);
  const [isStoringSelected, setIsStoringSelected] = useState(false);
  const [showModelSelector, setShowModelSelector] = useState(false);

  // Symbol selection state
  const [availableSymbols, setAvailableSymbols] = useState([]);
  const [selectedSymbols, setSelectedSymbols] = useState(["BTC/USDT", "ETH/USDT", "SOL/USDT", "TRX/USDT", "XRP/USDT"]);
  const [loadingSymbols, setLoadingSymbols] = useState(false);

  // Top models summary (fast-load)
  const [topModels, setTopModels] = useState([]);
  const [topModelsError, setTopModelsError] = useState(null);
  const [topModelsUpdatedAt, setTopModelsUpdatedAt] = useState(null);

  const navigate = useNavigate();

  const normalizeSymbols = useCallback((symbols) => {
    if (Array.isArray(symbols)) {
      return symbols.filter(Boolean);
    }
    if (typeof symbols === 'string') {
      return symbols
        .split(/[,+]/)
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
  }, []);

  const normalizeIndicators = useCallback((indicators, currentIndicators) => {
    const base = currentIndicators || {};
    if (!indicators) return base;

    if (Array.isArray(indicators)) {
      const indicatorSet = new Set(indicators);
      return Object.keys(base).reduce((acc, key) => {
        acc[key] = indicatorSet.has(key);
        return acc;
      }, {});
    }

    if (typeof indicators === 'string') {
      const parts = indicators
        .split(/[+,]/)
        .map((s) => s.trim())
        .filter(Boolean);
      const indicatorSet = new Set(parts);
      return Object.keys(base).reduce((acc, key) => {
        acc[key] = indicatorSet.has(key);
        return acc;
      }, {});
    }

    if (typeof indicators === 'object') {
      return Object.keys(base).reduce((acc, key) => {
        acc[key] = indicators[key] !== undefined ? Boolean(indicators[key]) : Boolean(base[key]);
        return acc;
      }, {});
    }

    return base;
  }, []);

  useEffect(() => {
    loadTrainingSessions();
    const interval = setInterval(loadTrainingSessions, 60000); // Refresh every 60 seconds
    return () => clearInterval(interval);
  }, []);

  // Prefill indicators based on backend defaults (plan/admin aware)
  useEffect(() => {
    const loadIndicatorDefaults = async () => {
      try {
        const res = await getIndicatorDefaults();
        if (res && res.indicators) {
          setEvolutionaryConfig(prev => ({
            ...prev,
            indicators: normalizeIndicators(res.indicators, prev.indicators)
          }));
        }
      } catch (err) {
        console.warn('Failed to load indicator defaults; using local defaults', err);
      }
    };
    loadIndicatorDefaults();
  }, [normalizeIndicators]);

  // Load top models summary on mount
  useEffect(() => {
    const loadTop = async () => {
      try {
        const res = await listTopModelsSummary();
        setTopModels(res.models || []);
        setTopModelsError(null);
        setTopModelsUpdatedAt(Date.now());
      } catch (err) {
        setTopModels([]);
        setTopModelsError(err.message || 'Failed to load top models summary');
      }
    };
    loadTop();
  }, []);

  // Update evolutionary config when selectedSymbols changes
  useEffect(() => {
    setEvolutionaryConfig(prev => ({
      ...prev,
      symbols: selectedSymbols
    }));
  }, [selectedSymbols]);

  // Load session details when selected session changes
  useEffect(() => {
    if (selectedSession) {
      loadSessionDetails(selectedSession);
    }
  }, [selectedSession]);

  // Load evolutionary experiments on mount
  useEffect(() => {
    loadEvolutionaryExperiments();
    const interval = setInterval(loadEvolutionaryExperiments, 60000); // Refresh every 60 seconds
    return () => clearInterval(interval);
  }, []);

  // Load available symbols on mount
  useEffect(() => {
    loadAvailableSymbols();
  }, []);

  // Load experiment details when selected experiment changes
  useEffect(() => {
    if (selectedExperiment) {
      loadExperimentDetails(selectedExperiment);
    } else {
      setExperimentDetails(null);
    }
  }, [selectedExperiment]);

  const loadTrainingSessions = async () => {
    try {
      const sessions = await listTrainingSessions();
      setActiveSessions(sessions.sessions || []);
      setSessionsUpdatedAt(Date.now());
    } catch (err) {
      console.error('Failed to load training sessions:', err);
    }
  };

  const loadSessionDetails = async (sessionId) => {
    try {
      const details = await getTrainingStatus(sessionId);
      setSessionDetails(details);
    } catch (err) {
      console.error('Failed to load session details:', err);
    }
  };

  const handleStartTraining = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await startRLTraining(trainingConfig);
      setIsTraining(true);

      // Reload sessions to show the new one
      await loadTrainingSessions();

      // Auto-select the new session
      if (result.session_id) {
        setSelectedSession(result.session_id);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = useCallback((field, value) => {
    setTrainingConfig(prev => ({
      ...prev,
      [field]: field === 'total_timesteps' || field === 'batch_size' || field === 'verbose'
        ? parseInt(value)
        : parseFloat(value)
    }));
  }, []);

  const loadEvolutionaryExperiments = async () => {
    try {
      const experiments = await listEvolutionaryExperiments();
      setActiveExperiments(experiments.experiments || []);
      setExperimentsUpdatedAt(Date.now());
    } catch (err) {
      console.error('Failed to load evolutionary experiments:', err);
    }
  };

  const loadExperimentDetails = async (experimentId) => {
    try {
      const details = await getEvolutionaryStatus(experimentId);
      setExperimentDetails(details);
    } catch (err) {
      console.error('Failed to load experiment details:', err);
      setExperimentDetails(null); // Clear details on error
    }
  };

  const loadAvailableSymbols = async () => {
    try {
      setLoadingSymbols(true);
      const symbolsData = await getAvailableSymbols();
      setAvailableSymbols(symbolsData.symbols || []);
      console.log('Loaded available symbols:', symbolsData.symbols);
    } catch (err) {
      console.error('Failed to load available symbols:', err);
      // Keep default symbols if API fails
      setAvailableSymbols(["BTC/USDT", "ETH/USDT", "SOL/USDT", "TRX/USDT", "XRP/USDT"]);
    } finally {
      setLoadingSymbols(false);
    }
  };

  const handleEvolutionaryConfigChange = useCallback((field, value) => {
    setEvolutionaryConfig(prev => ({
      ...prev,
      [field]: ['population_size', 'generations', 'total_timesteps'].includes(field)
        ? parseInt(value)
        : ['elitism_rate', 'mutation_rate', 'initial_balance', 'transaction_fee', 'max_position_size'].includes(field)
        ? parseFloat(value)
        : field === 'combinatorial_testing'
        ? value === 'true' || value === true
        : value
    }));
  }, []);

  const handleIndicatorChange = (indicator, checked) => {
    setEvolutionaryConfig(prev => ({
      ...prev,
      indicators: {
        ...prev.indicators,
        [indicator]: checked
      }
    }));
  };

  const handleStartEvolutionaryTraining = async () => {
    try {
      setEvolutionaryLoading(true);
      setEvolutionaryError(null);

      const result = await startEvolutionaryTraining(evolutionaryConfig);
      setIsEvolutionaryTraining(true);

      // Reload experiments to show the new one
      await loadEvolutionaryExperiments();

      // Auto-select the new experiment
      if (result.experiment_id) {
        setSelectedExperiment(result.experiment_id);
      }

    } catch (err) {
      setEvolutionaryError(err.message);
    } finally {
      setEvolutionaryLoading(false);
    }
  };

  const handleDeployCombinatorialBest = async () => {
    if (!selectedExperiment) return;

    try {
      const result = await deployCombinatorialBest(selectedExperiment);
      alert(`✅ Best combinatorial model deployed successfully!\n\nModel ID: ${result.model_id}\nBest Combination: ${result.best_combination.join(' + ')}\nSharpe Ratio: ${result.sharpe_ratio}`);

      // Optionally reload experiments to show updated status
      await loadEvolutionaryExperiments();
    } catch (err) {
      alert(`❌ Failed to deploy best model: ${err.message}`);
    }
  };

  const handleDeploySelectedModels = async () => {
    if (!selectedExperiment || selectedCombinations.length === 0) {
      alert('Please select at least one model combination to deploy.');
      return;
    }

    try {
      setIsDeployingSelected(true);
      const result = await deploySelectedModels(selectedExperiment, selectedCombinations);
      alert(`✅ Successfully deployed ${result.deployed_models.length} selected models!`);

      // Clear selection and close selector
      setSelectedCombinations([]);
      setShowModelSelector(false);

      // Reload experiments to show updated status
      await loadEvolutionaryExperiments();
    } catch (err) {
      alert(`❌ Failed to deploy selected models: ${err.message}`);
    } finally {
      setIsDeployingSelected(false);
    }
  };

  const handleDeleteExperiment = async (experimentId) => {
    if (!experimentId) return;
    const confirmDelete = window.confirm(`Delete experiment ${experimentId} and its models?`);
    if (!confirmDelete) return;
    try {
      setDeletingExperiment(true);
      setEvolutionaryError(null);
      await deleteEvolutionaryExperiment(experimentId);
      await loadEvolutionaryExperiments();
      setSelectedExperiment(null);
    } catch (err) {
      console.error('Failed to delete experiment:', err);
      setEvolutionaryError('Failed to delete experiment');
    } finally {
      setDeletingExperiment(false);
    }
  };

  const handleStopExperiment = async (experimentId) => {
    if (!experimentId) return;
    try {
      setStoppingExperiment(true);
      setEvolutionaryError(null);
      await stopEvolutionaryExperiment(experimentId);
      await loadEvolutionaryExperiments();
    } catch (err) {
      console.error('Failed to stop experiment:', err);
      setEvolutionaryError('Failed to stop experiment');
    } finally {
      setStoppingExperiment(false);
    }
  };

  const handleStoreSelectedModels = async () => {
    if (!selectedExperiment || selectedCombinations.length === 0) {
      alert('Please select at least one completed combination to store.');
      return;
    }
    try {
      setIsStoringSelected(true);
      setEvolutionaryError(null);

      // Build payload with selected combinations and their metrics
      const rankings = experimentDetails?.combinatorial_results?.combination_rankings || [];
      const selectedPayload = rankings
        .filter((combo) => {
          const indicators = combo.indicators || combo.combination || [];
          return selectedCombinations.some(
            (sel) => sel.join(',') === indicators.join(',')
          );
        })
        .map((combo) => ({
          indicators: combo.indicators || combo.combination || [],
          performance: {
            sharpe_ratio: combo.sharpe_ratio,
            win_rate: combo.win_rate,
            max_drawdown: combo.max_drawdown,
            net_pnl: combo.net_pnl,
            total_trades: combo.performance?.total_trades,
            end_balance: combo.performance?.end_balance,
          },
          experiment_id: selectedExperiment,
          timeframe: experimentDetails?.data_info?.timeframe || experimentDetails?.config?.timeframe,
          symbols: experimentDetails?.data_info?.symbols || experimentDetails?.config?.symbols,
        }));

      const res = await storeSelectedModels({
        experiment_id: selectedExperiment,
        combinations: selectedPayload,
        timeframe: experimentDetails?.data_info?.timeframe || experimentDetails?.config?.timeframe,
        symbols: experimentDetails?.data_info?.symbols || experimentDetails?.config?.symbols,
      });

      alert(
        `✅ Stored ${res.stored_count} model(s) (plan: ${res.plan}${
          res.limit ? `, limit ${res.limit}` : ', unlimited'
        })`
      );
    } catch (err) {
      console.error('Failed to store selected models:', err);
      alert(`❌ Failed to store models: ${err.message || err}`);
    } finally {
      setIsStoringSelected(false);
    }
  };

  const handleCombinationSelection = (combination, checked) => {
    if (checked) {
      setSelectedCombinations(prev => [...prev, combination]);
    } else {
      setSelectedCombinations(prev => prev.filter(combo => JSON.stringify(combo) !== JSON.stringify(combination)));
    }
  };

  const handleCombinationSelect = (combination, checked) => {
    const comboKey = combination.join(',');
    if (checked) {
      setSelectedCombinations(prev => [...prev, combination]);
    } else {
      setSelectedCombinations(prev => prev.filter(combo => combo.join(',') !== comboKey));
    }
  };

  const handleSymbolSelection = (symbol, checked) => {
    if (checked) {
      setSelectedSymbols(prev => [...prev, symbol]);
    } else {
      setSelectedSymbols(prev => prev.filter(s => s !== symbol));
    }
  };

  const handleSelectAllSymbols = () => {
    setSelectedSymbols(availableSymbols);
  };

  const handleClearSymbolSelection = () => {
    setSelectedSymbols([]);
  };

  const handleSelectAllCompleted = () => {
    if (!experimentDetails?.combinatorial_results?.combination_rankings) return;

    const completedCombos = experimentDetails.combinatorial_results.combination_rankings
      .filter(combo => combo.status === 'completed')
      .map(combo => combo.indicators || combo.combination || []);

    setSelectedCombinations(completedCombos);
  };

  const handleClearSelection = () => {
    setSelectedCombinations([]);
  };

  const handleRunLearningRateTests = async () => {
    const learningRates = [0.00025, 0.0005, 0.00075];
    setIsLearningRateTesting(true);
    setLearningRateResults(null);

    try {
      const experiments = [];

      // Run 3 experiments with different learning rates
      for (let i = 0; i < learningRates.length; i++) {
        const lr = learningRates[i];
        const config = {
          ...evolutionaryConfig,
          learning_rate: lr,
          combinatorial_testing: false  // Disable for individual learning rate tests
        };

        console.log(`Starting learning rate test ${i + 1}/3 with LR: ${lr}`);

        try {
          const result = await startEvolutionaryTraining(config);
          experiments.push({
            learning_rate: lr,
            experiment_id: result.experiment_id,
            status: 'running',
            start_time: Date.now()
          });

          // Small delay between starting experiments
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (err) {
          console.error(`Failed to start experiment with LR ${lr}:`, err);
          experiments.push({
            learning_rate: lr,
            experiment_id: null,
            status: 'failed',
            error: err.message
          });
        }
      }

      setLearningRateExperiments(experiments);

      // Start monitoring the experiments
      monitorLearningRateExperiments(experiments);

    } catch (err) {
      console.error('Failed to run learning rate tests:', err);
      setEvolutionaryError(err.message);
    } finally {
      setIsLearningRateTesting(false);
    }
  };

  const monitorLearningRateExperiments = async (experiments) => {
    const checkStatus = async () => {
      let allCompleted = true;
      const updatedExperiments = await Promise.all(
        experiments.map(async (exp) => {
          if (exp.status === 'failed' || !exp.experiment_id) return exp;

          try {
            const details = await getEvolutionaryStatus(exp.experiment_id);
            const status = details.status;

            if (status === 'completed') {
              return {
                ...exp,
                status: 'completed',
                performance_metrics: details.performance_metrics,
                completion_time: Date.now()
              };
            } else if (status === 'running') {
              allCompleted = false;
              return {
                ...exp,
                status: 'running',
                progress: details.progress
              };
            } else {
              return { ...exp, status };
            }
          } catch (err) {
            console.error(`Error checking status for LR ${exp.learning_rate}:`, err);
            return { ...exp, status: 'error', error: err.message };
          }
        })
      );

      setLearningRateExperiments(updatedExperiments);

      if (allCompleted) {
        // Generate comparison results
        generateLearningRateComparison(updatedExperiments);
      } else {
        // Continue monitoring
        setTimeout(checkStatus, 5000); // Check every 5 seconds
      }
    };

    checkStatus();
  };

  const generateLearningRateComparison = (experiments) => {
    const completedExperiments = experiments.filter(exp => exp.status === 'completed');

    if (completedExperiments.length === 0) {
      setLearningRateResults({ error: 'No experiments completed successfully' });
      return;
    }

    const comparison = {
      timestamp: new Date().toISOString(),
      experiments: completedExperiments.map(exp => ({
        learning_rate: exp.learning_rate,
        experiment_id: exp.experiment_id,
        performance_metrics: exp.performance_metrics,
        training_duration: (exp.completion_time - exp.start_time) / 1000 // seconds
      })),
      best_learning_rate: null,
      summary: {}
    };

    // Find best learning rate by Sharpe ratio
    let bestExp = null;
    let bestSharpe = -Infinity;

    completedExperiments.forEach(exp => {
      const sharpe = exp.performance_metrics?.sharpe_ratio || 0;
      if (sharpe > bestSharpe) {
        bestSharpe = sharpe;
        bestExp = exp;
      }
    });

    if (bestExp) {
      comparison.best_learning_rate = bestExp.learning_rate;
      comparison.best_performance = bestExp.performance_metrics;
    }

    // Generate summary statistics
    comparison.summary = {
      total_experiments: experiments.length,
      completed_experiments: completedExperiments.length,
      failed_experiments: experiments.length - completedExperiments.length,
      learning_rates_tested: experiments.map(exp => exp.learning_rate),
      sharpe_range: completedExperiments.length > 0 ? {
        min: Math.min(...completedExperiments.map(exp => exp.performance_metrics?.sharpe_ratio || 0)),
        max: Math.max(...completedExperiments.map(exp => exp.performance_metrics?.sharpe_ratio || 0)),
        avg: completedExperiments.reduce((sum, exp) => sum + (exp.performance_metrics?.sharpe_ratio || 0), 0) / completedExperiments.length
      } : null
    };

    setLearningRateResults(comparison);

    // Reload experiments list to show the new ones
    loadEvolutionaryExperiments();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'running': return '#ed8936';
      case 'completed': return '#48bb78';
      case 'failed': return '#f56565';
      default: return '#718096';
    }
  };

  const topPresetModel = useMemo(() => (topModels && topModels.length > 0 ? topModels[0] : null), [topModels]);
  const presetSymbols = normalizeSymbols(topPresetModel?.symbols);
  const symbolMismatch = presetSymbols.length > 0 && selectedSymbols.some(s => !presetSymbols.includes(s));

  const applyPresetFromModel = (model) => {
    if (!model) return;

    const normalizedSymbols = normalizeSymbols(model.symbols);

    if (normalizedSymbols.length > 0) {
      setSelectedSymbols(normalizedSymbols);
    }

    setEvolutionaryConfig((prev) => ({
      ...prev,
      timeframe: model.timeframe || prev.timeframe,
      indicators: normalizeIndicators(model.indicators, prev.indicators),
    }));
  };

  return (
    <div className="rl-training">
      <div className="rl-training-grid">
        {/* Top Models Summary */}
        <div className="rl-training-section">
          <h3>🏅 Top Models Snapshot</h3>
          {topModelsError ? (
            <div className="rl-alert rl-alert-warning">
              <span className="alert-icon">⚠️</span>
              {topModelsError}
            </div>
          ) : topModels.length === 0 ? (
            <div className="rl-alert rl-alert-info">
              Loading top models summary...
            </div>
          ) : (
            <>
              <div className="rl-top-models-grid">
                {topModels.slice(0, 5).map((m, idx) => (
                  <div key={m.id || m.model_id || idx} className="rl-top-model-card">
                    <div className="rl-top-model-header">
                      <div className="rl-top-model-name">{m.name}</div>
                      <div className="rl-top-model-metric">Sharpe {m.performance?.sharpe_ratio?.toFixed(2) ?? 'N/A'}</div>
                    </div>
                    <div className="rl-top-model-body">
                      <div><strong>Symbols:</strong> {Array.isArray(m.symbols) ? m.symbols.join(', ') : 'N/A'}</div>
                      <div><strong>Timeframe:</strong> {m.timeframe || 'N/A'}</div>
                      <div><strong>Indicators:</strong> {m.indicators?.length || 0}</div>
                      <div><strong>Return:</strong> {((m.performance?.total_return ?? 0) * 100).toFixed(1)}%</div>
                      <div><strong>Max DD:</strong> {((m.performance?.max_drawdown ?? 0) * 100).toFixed(1)}%</div>
                    </div>
                    <div className="rl-top-model-actions">
                      <button
                        className="rl-btn rl-btn-xs rl-btn-primary"
                        onClick={() => applyPresetFromModel(m)}
                      >
                        Use as preset
                      </button>
                      <button
                        className="rl-btn rl-btn-xs rl-btn-secondary"
                      onClick={() => navigate('/models')}
                      >
                        View model
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="rl-top-model-footer">
                <small>Updated {topModelsUpdatedAt ? new Date(topModelsUpdatedAt).toLocaleTimeString() : '—'}</small>
              </div>
            </>
          )}
        </div>

        {/* Training Configuration */}
        <div className="rl-training-section">
          <h3>🎯 Standard Training Configuration</h3>

          <div className="rl-config-form">
            <div className="rl-config-group">
              <label>Algorithm:</label>
              <select
                value={trainingConfig.algorithm}
                onChange={(e) => handleConfigChange('algorithm', e.target.value)}
                disabled={isTraining}
              >
                <option value="PPO">PPO (Proximal Policy Optimization)</option>
                <option value="A2C">A2C (Advantage Actor-Critic)</option>
                <option value="SAC">SAC (Soft Actor-Critic)</option>
              </select>
            </div>

            <div className="rl-config-group">
              <label>Total Timesteps:</label>
              <input
                type="number"
                value={trainingConfig.total_timesteps}
                onChange={(e) => handleConfigChange('total_timesteps', e.target.value)}
                min="1000"
                max="1000000"
                step="1000"
                disabled={isTraining}
              />
            </div>

            <div className="rl-config-row">
              <div className="rl-config-group">
                <label>Learning Rate:</label>
                <input
                  type="number"
                  value={trainingConfig.learning_rate}
                  onChange={(e) => handleConfigChange('learning_rate', e.target.value)}
                  min="0.00001"
                  max="0.01"
                  step="0.0001"
                  disabled={isTraining}
                />
              </div>

              <div className="rl-config-group">
                <label>Batch Size:</label>
                <input
                  type="number"
                  value={trainingConfig.batch_size}
                  onChange={(e) => handleConfigChange('batch_size', e.target.value)}
                  min="16"
                  max="1024"
                  step="16"
                  disabled={isTraining}
                />
              </div>
            </div>

            <div className="rl-config-row">
              <div className="rl-config-group">
                <label>Gamma (Discount):</label>
                <input
                  type="number"
                  value={trainingConfig.gamma}
                  onChange={(e) => handleConfigChange('gamma', e.target.value)}
                  min="0.8"
                  max="0.999"
                  step="0.001"
                  disabled={isTraining}
                />
              </div>

              <div className="rl-config-group">
                <label>Verbosity:</label>
                <select
                  value={trainingConfig.verbose}
                  onChange={(e) => handleConfigChange('verbose', e.target.value)}
                  disabled={isTraining}
                >
                  <option value="0">Silent</option>
                  <option value="1">Progress</option>
                  <option value="2">Debug</option>
                </select>
              </div>
            </div>

            {error && (
              <div className="rl-error-message">
                ❌ {error}
              </div>
            )}

            <button
              onClick={handleStartTraining}
              disabled={loading || isTraining}
              className="rl-start-training-btn"
            >
              {loading ? '🚀 Starting Training...' : '🚀 Start Training'}
            </button>
          </div>
        </div>

        {/* Evolutionary Training Configuration */}
        <div className="rl-training-section">
          <h3>🧬 Evolutionary Training Configuration</h3>
          <p style={{fontSize: '0.9rem', color: '#666', marginBottom: '1rem'}}>
            Population-based training that evolves hyperparameters across generations,
            learning from successful lineages and avoiding poor parameter spaces.
          </p>

          <div className="rl-config-form">
            <div className="rl-config-row">
              <div className="rl-config-group">
                <label>Population Size:</label>
                <input
                  type="number"
                  value={evolutionaryConfig.population_size}
                  onChange={(e) => handleEvolutionaryConfigChange('population_size', e.target.value)}
                  min="4"
                  max="20"
                  step="2"
                  disabled={isEvolutionaryTraining}
                />
              </div>

              <div className="rl-config-group">
                <label>Generations:</label>
                <input
                  type="number"
                  value={evolutionaryConfig.generations}
                  onChange={(e) => handleEvolutionaryConfigChange('generations', e.target.value)}
                  min="3"
                  max="10"
                  step="1"
                  disabled={isEvolutionaryTraining}
                />
              </div>
            </div>

            <div className="rl-config-row">
              <div className="rl-config-group">
                <label>Elitism Rate:</label>
                <input
                  type="number"
                  value={evolutionaryConfig.elitism_rate}
                  onChange={(e) => handleEvolutionaryConfigChange('elitism_rate', e.target.value)}
                  min="0.1"
                  max="0.5"
                  step="0.05"
                  disabled={isEvolutionaryTraining}
                />
              </div>

              <div className="rl-config-group">
                <label>Mutation Rate:</label>
                <input
                  type="number"
                  value={evolutionaryConfig.mutation_rate}
                  onChange={(e) => handleEvolutionaryConfigChange('mutation_rate', e.target.value)}
                  min="0.1"
                  max="0.8"
                  step="0.1"
                  disabled={isEvolutionaryTraining}
                />
              </div>
            </div>

            <div className="rl-config-row">
              <div className="rl-config-group">
                <label>Timesteps per Individual:</label>
                <input
                  type="number"
                  value={evolutionaryConfig.total_timesteps}
                  onChange={(e) => handleEvolutionaryConfigChange('total_timesteps', e.target.value)}
                  min="10000"
                  max="200000"
                  step="10000"
                  disabled={isEvolutionaryTraining}
                />
              </div>

              <div className="rl-config-group">
                <label>Initial Balance:</label>
                <input
                  type="number"
                  value={evolutionaryConfig.initial_balance}
                  onChange={(e) => handleEvolutionaryConfigChange('initial_balance', e.target.value)}
                  min="1000"
                  max="50000"
                  step="1000"
                  disabled={isEvolutionaryTraining}
                />
              </div>
            </div>

            <div className="rl-config-row">
              <div className="rl-config-group">
                <label>Timeframe:</label>
                <select
                  value={evolutionaryConfig.timeframe}
                  onChange={(e) => handleEvolutionaryConfigChange('timeframe', e.target.value)}
                  disabled={isEvolutionaryTraining}
                >
                  <option value="15m">15 Minutes (Scalping)</option>
                  <option value="1h">1 Hour (Day Trading)</option>
                  <option value="4h">4 Hours (Swing Trading)</option>
                  <option value="1d">1 Day (Position Trading)</option>
                </select>
              </div>

              <div className="rl-config-group">
                <label>Transaction Fee:</label>
                <input
                  type="number"
                  value={evolutionaryConfig.transaction_fee}
                  onChange={(e) => handleEvolutionaryConfigChange('transaction_fee', e.target.value)}
                  min="0.0001"
                  max="0.01"
                  step="0.0001"
                  disabled={isEvolutionaryTraining}
                />
              </div>
            </div>

            {/* Symbol Selection */}
            <div className="rl-symbols-selection">
              <h4>📊 Trading Symbols</h4>
              <p style={{fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem'}}>
                Select which symbols to use for training. More symbols = more diverse training data but longer training time.
              </p>

              {loadingSymbols ? (
                <div style={{textAlign: 'center', padding: '1rem'}}>
                  <div>🔄 Loading available symbols...</div>
                </div>
              ) : (
                <div className="rl-symbols-controls">
                  <div className="rl-symbols-info">
                    <span>Available: {availableSymbols.length} | Selected: {selectedSymbols.length}</span>
                    {presetSymbols.length > 0 && (
                      <span className="rl-symbols-preset">
                        Preset symbols: {presetSymbols.join(', ')}
                      </span>
                    )}
                    <div className="rl-symbols-buttons">
                      <button
                        onClick={handleSelectAllSymbols}
                        className="rl-select-all-symbols-btn"
                        disabled={isEvolutionaryTraining}
                      >
                        Select All
                      </button>
                      <button
                        onClick={handleClearSymbolSelection}
                        className="rl-clear-symbols-btn"
                        disabled={isEvolutionaryTraining}
                      >
                        Clear All
                      </button>
                    </div>
                  </div>

                  <div className="rl-symbols-grid">
                    {availableSymbols.map(symbol => (
                      <label key={symbol} className="rl-symbol-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedSymbols.includes(symbol)}
                          onChange={(e) => handleSymbolSelection(symbol, e.target.checked)}
                          disabled={isEvolutionaryTraining}
                        />
                        <span className="rl-symbol-label">
                          <strong>{symbol.replace('/USDT', '')}</strong>
                        </span>
                      </label>
                    ))}
                  </div>

                  {selectedSymbols.length === 0 && (
                    <div className="rl-warning-message" style={{color: '#f56565', fontSize: '0.9rem', marginTop: '0.5rem'}}>
                      ⚠️ Please select at least one symbol for training
                    </div>
                  )}
                  {symbolMismatch && (
                    <div className="rl-warning-message" style={{color: '#ed8936', fontSize: '0.9rem', marginTop: '0.5rem'}}>
                      ⚠️ Selected symbols differ from preset best-model symbols. Consider aligning to: {presetSymbols.join(', ')}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Technical Indicators Selection */}
            <div className="rl-indicators-selection">
              <h4>🧮 Technical Indicators</h4>
              <p style={{fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem'}}>
                Select which indicators to use for signal generation:
              </p>

              {/* Current Indicators */}
              <div className="rl-indicator-category">
                <h5>📊 Current Indicators</h5>
                <div className="rl-indicators-grid">
                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.MFI}
                      onChange={(e) => handleIndicatorChange('MFI', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>MFI</strong> - Money Flow Index (Volume-based momentum)
                    </span>
                  </label>

                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.VWAP}
                      onChange={(e) => handleIndicatorChange('VWAP', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>VWAP</strong> - Volume Weighted Average Price
                    </span>
                  </label>

                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.GM_Confluence}
                      onChange={(e) => handleIndicatorChange('GM_Confluence', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>GM_Confluence</strong> - Geometric Mean Phi369 (Sacred Geometry)
                    </span>
                  </label>
                </div>
              </div>

              {/* Breakout Indicators */}
              <div className="rl-indicator-category">
                <h5>📈 Breakout Indicators</h5>
                <div className="rl-indicators-grid">
                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.Donchian_Channels}
                      onChange={(e) => handleIndicatorChange('Donchian_Channels', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>Donchian Channels</strong> - Price channel breakouts
                    </span>
                  </label>

                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.Keltner_Channels}
                      onChange={(e) => handleIndicatorChange('Keltner_Channels', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>Keltner Channels</strong> - Volatility-based breakouts
                    </span>
                  </label>

                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.Price_Channels}
                      onChange={(e) => handleIndicatorChange('Price_Channels', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>Price Channels</strong> - Trend channel breakouts
                    </span>
                  </label>

                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.Volume_Breakouts}
                      onChange={(e) => handleIndicatorChange('Volume_Breakouts', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>Volume Breakouts</strong> - Volume confirmation signals
                    </span>
                  </label>
                </div>
              </div>

              {/* Timing Model Indicators */}
              <div className="rl-indicator-category">
                <h5>⏰ Timing Model Indicators</h5>
                <div className="rl-indicators-grid">
                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.Fibonacci_Extensions}
                      onChange={(e) => handleIndicatorChange('Fibonacci_Extensions', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>Fibonacci Extensions</strong> - Projection targets
                    </span>
                  </label>

                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.Gann_Angles}
                      onChange={(e) => handleIndicatorChange('Gann_Angles', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>Gann Angles</strong> - Geometric timing
                    </span>
                  </label>

                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.Andrews_Pitchfork}
                      onChange={(e) => handleIndicatorChange('Andrews_Pitchfork', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>Andrews Pitchfork</strong> - Median line timing
                    </span>
                  </label>

                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.Wolfe_Waves}
                      onChange={(e) => handleIndicatorChange('Wolfe_Waves', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>Wolfe Waves</strong> - Harmonic patterns
                    </span>
                  </label>
                </div>
              </div>

              {/* Momentum Timing Indicators */}
              <div className="rl-indicator-category">
                <h5>📊 Momentum Timing Indicators</h5>
                <div className="rl-indicators-grid">
                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.Williams_Alligator}
                      onChange={(e) => handleIndicatorChange('Williams_Alligator', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>Williams Alligator</strong> - Trend timing
                    </span>
                  </label>

                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.Fractal_Dimension}
                      onChange={(e) => handleIndicatorChange('Fractal_Dimension', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>Fractal Dimension</strong> - Market efficiency
                    </span>
                  </label>

                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.Hurst_Exponent}
                      onChange={(e) => handleIndicatorChange('Hurst_Exponent', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>Hurst Exponent</strong> - Trend persistence
                    </span>
                  </label>
                </div>
              </div>

              {/* Volume Timing Indicators */}
              <div className="rl-indicator-category">
                <h5>📊 Volume Timing Indicators</h5>
                <div className="rl-indicators-grid">
                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.Volume_Profile}
                      onChange={(e) => handleIndicatorChange('Volume_Profile', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>Volume Profile</strong> - Price levels by volume
                    </span>
                  </label>

                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.Order_Flow}
                      onChange={(e) => handleIndicatorChange('Order_Flow', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>Order Flow</strong> - Buying/selling pressure
                    </span>
                  </label>

                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.POC}
                      onChange={(e) => handleIndicatorChange('POC', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>POC</strong> - Point of Control (highest volume price level)
                    </span>
                  </label>

                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.CVD}
                      onChange={(e) => handleIndicatorChange('CVD', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>CVD</strong> - Cumulative Volume Delta (buying vs selling pressure)
                    </span>
                  </label>
                </div>
              </div>

              {/* Market Structure Layer */}
              <div className="rl-indicator-category">
                <h5>🏗️ Market Structure Layer</h5>
                <div className="rl-indicators-grid">
                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.Swing_High_Low}
                      onChange={(e) => handleIndicatorChange('Swing_High_Low', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>Swing High/Low</strong> - Pivot point detection and distance measurements
                    </span>
                  </label>

                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.Order_Blocks}
                      onChange={(e) => handleIndicatorChange('Order_Blocks', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>Order Blocks</strong> - Institutional order placement zones after strong moves
                    </span>
                  </label>

                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.Fair_Value_Gaps}
                      onChange={(e) => handleIndicatorChange('Fair_Value_Gaps', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>Fair Value Gaps</strong> - Price imbalances between buying/selling pressure
                    </span>
                  </label>

                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.Liquidity_Pools}
                      onChange={(e) => handleIndicatorChange('Liquidity_Pools', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>Liquidity Pools</strong> - Areas of concentrated liquidity (equal highs/lows)
                    </span>
                  </label>

                  <label className="rl-indicator-checkbox">
                    <input
                      type="checkbox"
                      checked={evolutionaryConfig.indicators.Market_Structure}
                      onChange={(e) => handleIndicatorChange('Market_Structure', e.target.checked)}
                      disabled={isEvolutionaryTraining}
                    />
                    <span className="rl-indicator-label">
                      <strong>Market Structure</strong> - State classification (accumulation/expansion/distribution/reversion)
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Combinatorial Testing Option */}
            <div className="rl-combinatorial-testing">
              <label className="rl-testing-checkbox">
                <input
                  type="checkbox"
                  checked={evolutionaryConfig.combinatorial_testing}
                  onChange={(e) => handleEvolutionaryConfigChange('combinatorial_testing', e.target.checked)}
                  disabled={isEvolutionaryTraining}
                />
                <span className="rl-testing-label">
                  <strong>🔬 Combinatorial Testing</strong> - Test all possible indicator combinations automatically
                </span>
              </label>
              {evolutionaryConfig.combinatorial_testing && (
                <div className="rl-combinatorial-info">
                  <small style={{color: '#666'}}>
                    This will run {Math.pow(2, Object.keys(evolutionaryConfig.indicators).filter(k => evolutionaryConfig.indicators[k]).length) - 1} experiments
                    testing all combinations of selected indicators to find the optimal strategy.
                  </small>
                </div>
              )}
            </div>

            {evolutionaryError && (
              <div className="rl-error-message">
                ❌ {evolutionaryError}
              </div>
            )}

            <button
              onClick={handleStartEvolutionaryTraining}
              disabled={evolutionaryLoading || isEvolutionaryTraining}
              className="rl-start-training-btn"
              style={{background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)'}}
            >
              {evolutionaryLoading ? '🚀 Starting Evolutionary Training...' : '🧬 Start Evolutionary Training'}
            </button>

            {/* Learning Rate Testing Section */}
            <div className="rl-learning-rate-testing">
              <h4>🧪 Learning Rate Optimization Test</h4>
              <p className="rl-lr-copy">
                Test 3 different learning rates (0.00025, 0.0005, 0.00075) simultaneously to find the optimal learning rate for your configuration.
              </p>

              <div className="rl-learning-rate-info">
                <div className="rl-lr-chip-row">
                  <div className="rl-lr-chip">
                    <strong>LR 0.00025:</strong> Conservative learning (stable, slower convergence)
                  </div>
                  <div className="rl-lr-chip">
                    <strong>LR 0.0005:</strong> Balanced learning (good compromise)
                  </div>
                  <div className="rl-lr-chip">
                    <strong>LR 0.00075:</strong> Aggressive learning (faster, potentially unstable)
                  </div>
                </div>
              </div>

              <button
                onClick={handleRunLearningRateTests}
                disabled={isLearningRateTesting || isEvolutionaryTraining}
                className="rl-start-training-btn rl-lr-run-btn"
              >
                {isLearningRateTesting ? '🧪 Running Learning Rate Tests...' : '🧪 Run Learning Rate Tests (3 Experiments)'}
              </button>

              {/* Learning Rate Test Progress */}
              {learningRateExperiments.length > 0 && (
                <div className="rl-learning-rate-progress">
                  <h5>📊 Learning Rate Test Progress</h5>
                  <div className="rl-lr-experiments-grid">
                    {learningRateExperiments.map((exp, index) => (
                      <div key={index} className={`rl-lr-experiment-item status-${exp.status || 'unknown'}`}>
                        <div className="rl-lr-exp-header">
                          <span className="rl-lr-exp-title">
                            Learning Rate: {exp.learning_rate}
                          </span>
                          <span className={`rl-lr-status-badge status-${exp.status || 'unknown'}`}>
                            {exp.status?.toUpperCase()}
                          </span>
                        </div>

                        {exp.experiment_id && (
                          <div className="rl-lr-exp-id">
                            Experiment ID: {exp.experiment_id}
                          </div>
                        )}

                        {exp.status === 'completed' && exp.performance_metrics && (
                          <div className="rl-lr-exp-metrics">
                            <div><strong>Sharpe:</strong> {exp.performance_metrics.sharpe_ratio?.toFixed(3) || 'N/A'}</div>
                            <div><strong>Win Rate:</strong> {((exp.performance_metrics.win_rate || 0) * 100).toFixed(1)}%</div>
                            <div><strong>P&L:</strong> ${exp.performance_metrics.net_pnl?.toFixed(2) || '0.00'}</div>
                          </div>
                        )}

                        {exp.error && (
                          <div className="rl-lr-exp-error">
                            Error: {exp.error}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Learning Rate Comparison Results */}
              {learningRateResults && (
                <div className="rl-learning-rate-results">
                  <h5>🏆 Learning Rate Comparison Results</h5>

                  {learningRateResults.error ? (
                    <div className="rl-lr-error">
                      ❌ {learningRateResults.error}
                    </div>
                  ) : (
                    <div>
                      <div className="rl-lr-summary">
                        <h6>📈 Summary</h6>
                        <div className="rl-lr-summary-grid">
                          <div>
                            <strong>Best Learning Rate:</strong><br/>
                            <span className="rl-lr-best">
                              {learningRateResults.best_learning_rate}
                            </span>
                          </div>
                          <div>
                            <strong>Experiments Completed:</strong><br/>
                            <span className="rl-lr-strong">
                              {learningRateResults.summary.completed_experiments}/{learningRateResults.summary.total_experiments}
                            </span>
                          </div>
                          <div>
                            <strong>Sharpe Range:</strong><br/>
                            <span className="rl-lr-strong">
                              {learningRateResults.summary.sharpe_range?.min?.toFixed(3) || 'N/A'} - {learningRateResults.summary.sharpe_range?.max?.toFixed(3) || 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {learningRateResults.best_performance && (
                        <div className="rl-best-lr-performance">
                          <h6>🥇 Best Performance (LR: {learningRateResults.best_learning_rate})</h6>
                          <div className="rl-lr-best-metrics">
                            <div><strong>Sharpe Ratio:</strong> {learningRateResults.best_performance.sharpe_ratio?.toFixed(3) || 'N/A'}</div>
                            <div><strong>Win Rate:</strong> {((learningRateResults.best_performance.win_rate || 0) * 100).toFixed(1)}%</div>
                            <div><strong>Net P&L:</strong> ${learningRateResults.best_performance.net_pnl?.toFixed(2) || '0.00'}</div>
                            <div><strong>Max Drawdown:</strong> {((learningRateResults.best_performance.max_drawdown || 0) * 100).toFixed(2)}%</div>
                          </div>
                        </div>
                      )}

                      <div className="rl-lr-recommendation">
                        <h6>💡 Recommendation</h6>
                        <p className="rl-lr-copy">
                          Use learning rate <strong>{learningRateResults.best_learning_rate}</strong> for your evolutionary training.
                          This learning rate achieved the best Sharpe ratio of {learningRateResults.best_performance?.sharpe_ratio?.toFixed(3) || 'N/A'}
                          across the tested configurations.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Active Training Sessions */}
        <div className="rl-training-section">
          <div className="rl-section-header-row">
            <h3>📋 Active Sessions</h3>
            <div className="rl-section-actions">
              <small>Updated {sessionsUpdatedAt ? new Date(sessionsUpdatedAt).toLocaleTimeString() : '—'}</small>
              <button className="rl-btn rl-btn-xs rl-btn-secondary" onClick={loadTrainingSessions}>Refresh</button>
            </div>
          </div>

          <div className="rl-sessions-list">
            {activeSessions.length === 0 ? (
              <div className="rl-no-sessions">
                <p>No active training sessions</p>
                <small>Start a new training session to see it here</small>
              </div>
            ) : (
              activeSessions.map(session => (
                <div
                  key={session.session_id}
                  className={`rl-session-item ${selectedSession === session.session_id ? 'selected' : ''}`}
                  onClick={() => setSelectedSession(session.session_id)}
                >
                  <div className="rl-session-header">
                    <span className="rl-session-id">{session.session_id}</span>
                    <span
                      className="rl-session-status"
                      style={{ backgroundColor: getStatusColor(session.status) }}
                    >
                      {session.status?.toUpperCase()}
                    </span>
                  </div>
                  <div className="rl-session-info">
                    <span>{session.config?.algorithm || 'Unknown'}</span>
                    <span>{new Date(session.start_time * 1000).toLocaleString()}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Active Evolutionary Experiments */}
        <div className="rl-training-section">
          <div className="rl-section-header-row">
            <h3>🧬 Active Evolutionary Experiments</h3>
            <div className="rl-section-actions">
              <small>Updated {experimentsUpdatedAt ? new Date(experimentsUpdatedAt).toLocaleTimeString() : '—'}</small>
              <button className="rl-btn rl-btn-xs rl-btn-secondary" onClick={loadEvolutionaryExperiments}>Refresh</button>
            </div>
          </div>

          <div className="rl-sessions-list">
            {activeExperiments.length === 0 ? (
              <div className="rl-no-sessions">
                <p>No active evolutionary experiments</p>
                <small>Start an evolutionary training experiment to see it here</small>
              </div>
            ) : (
              activeExperiments.map(experiment => (
                <div
                  key={experiment.experiment_id}
                  className={`rl-session-item ${selectedExperiment === experiment.experiment_id ? 'selected' : ''}`}
                  onClick={() => setSelectedExperiment(experiment.experiment_id)}
                >
                  <div className="rl-session-header">
                    <span className="rl-session-id">{experiment.experiment_id}</span>
                    <span
                      className="rl-session-status"
                      style={{ backgroundColor: getStatusColor(experiment.status) }}
                    >
                      {experiment.status?.toUpperCase()}
                    </span>
                  </div>
                  <div className="rl-session-info">
                    <span>Pop: {experiment.config?.population_size || 'N/A'}</span>
                    <span>Gen: {experiment.progress?.current_generation || 0}/{experiment.config?.generations || 'N/A'}</span>
                    <span>{new Date(experiment.start_time * 1000).toLocaleString()}</span>
                  </div>
                  <div className="rl-session-actions">
                    <button
                      className="rl-btn rl-btn-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStopExperiment(experiment.experiment_id);
                      }}
                      disabled={stoppingExperiment}
                    >
                      {stoppingExperiment ? 'Stopping...' : 'Stop'}
                    </button>
                    <button
                      className="rl-btn rl-btn-xs rl-btn-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteExperiment(experiment.experiment_id);
                      }}
                      disabled={deletingExperiment}
                    >
                      {deletingExperiment ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Session Details */}
      {selectedSession && sessionDetails && (
        <div className="rl-training-section">
          <div className="rl-section-header-row">
            <h3>📊 Session Details: {selectedSession}</h3>
            <button className="rl-btn rl-btn-xs rl-btn-secondary" onClick={() => loadSessionDetails(selectedSession)}>
              Refresh
            </button>
          </div>

          <div className="rl-session-details">
            <div className="rl-detail-grid">
              <div className="rl-detail-item">
                <span className="rl-label">Status:</span>
                <span
                  className="rl-value"
                  style={{ color: getStatusColor(sessionDetails.status) }}
                >
                  {sessionDetails.status?.toUpperCase()}
                </span>
              </div>

              <div className="rl-detail-item">
                <span className="rl-label">Progress:</span>
                <div className="rl-progress-bar">
                  <div
                    className="rl-progress-fill"
                    style={{ width: `${(sessionDetails.progress || 0) * 100}%` }}
                  ></div>
                  <span className="rl-progress-text">
                    {((sessionDetails.progress || 0) * 100).toFixed(1)}%
                  </span>
                </div>
              </div>

              <div className="rl-detail-item">
                <span className="rl-label">Algorithm:</span>
                <span className="rl-value">{sessionDetails.config?.algorithm}</span>
              </div>

              <div className="rl-detail-item">
                <span className="rl-label">Total Timesteps:</span>
                <span className="rl-value">{sessionDetails.config?.total_timesteps?.toLocaleString()}</span>
              </div>

              <div className="rl-detail-item">
                <span className="rl-label">Learning Rate:</span>
                <span className="rl-value">{sessionDetails.config?.learning_rate}</span>
              </div>

              <div className="rl-detail-item">
                <span className="rl-label">Started:</span>
                <span className="rl-value">
                  {new Date(sessionDetails.start_time * 1000).toLocaleString()}
                </span>
              </div>
            </div>

            {sessionDetails.status === 'running' && (
              <div className="rl-training-animation">
                <div className="rl-brain-icon">🧠</div>
                <div className="rl-training-particles">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="rl-particle" style={{
                      animationDelay: `${i * 0.2}s`,
                      left: `${20 + i * 15}%`
                    }}></div>
                  ))}
                </div>
                <p>RL Agent is learning...</p>
              </div>
            )}

            {sessionDetails.status === 'completed' && (
              <div className="rl-success-message">
                ✅ Training completed successfully!
              </div>
            )}

            {/* Performance Metrics */}
            {sessionDetails.performance_metrics && (
              <div className="rl-performance-metrics">
                <h4>📊 Performance Results</h4>

                <div className="rl-metrics-grid">
                  <div className="rl-metric-item">
                    <span className="rl-metric-label">End Balance:</span>
                    <span className="rl-metric-value">${sessionDetails.performance_metrics.end_balance?.toFixed(2) || '0.00'}</span>
                  </div>

                  <div className="rl-metric-item">
                    <span className="rl-metric-label">Net P&L:</span>
                    <span className={`rl-metric-value ${sessionDetails.performance_metrics.net_pnl >= 0 ? 'positive' : 'negative'}`}>
                      {sessionDetails.performance_metrics.net_pnl >= 0 ? '+' : ''}${sessionDetails.performance_metrics.net_pnl?.toFixed(2) || '0.00'}
                    </span>
                  </div>

                  <div className="rl-metric-item">
                    <span className="rl-metric-label">Total Trades:</span>
                    <span className="rl-metric-value">{sessionDetails.performance_metrics.total_trades || 0}</span>
                  </div>

                  <div className="rl-metric-item">
                    <span className="rl-metric-label">Win Rate:</span>
                    <span className="rl-metric-value">{((sessionDetails.performance_metrics.win_rate || 0) * 100).toFixed(1)}%</span>
                  </div>

                  <div className="rl-metric-item">
                    <span className="rl-metric-label">Max Drawdown:</span>
                    <span className="rl-metric-value negative">{(sessionDetails.performance_metrics.max_drawdown * 100).toFixed(2)}%</span>
                  </div>

                  <div className="rl-metric-item">
                    <span className="rl-metric-label">Sharpe Ratio:</span>
                    <span className="rl-metric-value">{sessionDetails.performance_metrics.sharpe_ratio?.toFixed(2) || 'N/A'}</span>
                  </div>
                </div>

                {/* Per-Pair Statistics */}
                {sessionDetails.performance_metrics.per_pair_stats && Object.keys(sessionDetails.performance_metrics.per_pair_stats).length > 0 && (
                  <div className="rl-pair-stats">
                    <h5>📈 Per-Pair Performance</h5>
                    <div className="rl-pair-stats-grid">
                      {Object.entries(sessionDetails.performance_metrics.per_pair_stats).map(([pair, stats]) => (
                        <div key={pair} className="rl-pair-stat-item">
                          <div className="rl-pair-name">{pair.replace('/USDT', '')}</div>
                          <div className="rl-pair-metrics">
                            <span>Trades: {stats.trades || 0}</span>
                            <span>Win Rate: {((stats.win_rate || 0) * 100).toFixed(1)}%</span>
                            <span className={stats.pnl >= 0 ? 'positive' : 'negative'}>
                              P&L: {stats.pnl >= 0 ? '+' : ''}{(stats.pnl * 100).toFixed(2)}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Trade History */}
                {sessionDetails.trades && sessionDetails.trades.length > 0 && (
                  <div className="rl-trade-history">
                    <h5>📋 Recent Trades</h5>
                    <div className="rl-trades-table">
                      <div className="rl-table-header">
                        <span>Symbol</span>
                        <span>Side</span>
                        <span>Amount</span>
                        <span>Price</span>
                        <span>P&L %</span>
                        <span>Time</span>
                      </div>
                      {sessionDetails.trades.slice(0, 10).map((trade, index) => (
                        <div key={trade.id || index} className="rl-table-row">
                          <span className="rl-symbol">{trade.symbol?.replace('/USDT', '') || 'N/A'}</span>
                          <span className={`rl-trade-side ${trade.side?.toLowerCase() || ''}`}>
                            {trade.side?.toUpperCase() || 'N/A'}
                          </span>
                          <span>{trade.amount?.toFixed(6) || '0.000000'}</span>
                          <span>${trade.price?.toFixed(2) || '0.00'}</span>
                          <span className={trade.profit_loss_percent >= 0 ? 'positive' : 'negative'}>
                            {trade.profit_loss_percent >= 0 ? '+' : ''}{trade.profit_loss_percent?.toFixed(2) || '0.00'}%
                          </span>
                          <span>{new Date((trade.timestamp || 0) * 1000).toLocaleTimeString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Evolutionary Experiment Details */}
      {selectedExperiment && experimentDetails && (
        <div className="rl-training-section">
          <div className="rl-section-header-row">
            <h3>🧬 Experiment Details: {selectedExperiment}</h3>
            <button className="rl-btn rl-btn-xs rl-btn-secondary" onClick={() => loadExperimentDetails(selectedExperiment)}>
              Refresh
            </button>
          </div>

          <div className="rl-session-details">
            <div className="rl-detail-grid">
              <div className="rl-detail-item">
                <span className="rl-label">Status:</span>
                <span
                  className="rl-value"
                  style={{ color: getStatusColor(experimentDetails.status) }}
                >
                  {experimentDetails.status?.toUpperCase()}
                </span>
              </div>

              {/* Show different progress for combinatorial vs regular experiments */}
              {experimentDetails.data_info?.combinatorial_testing ? (
                <div className="rl-detail-item">
                  <span className="rl-label">Combination Progress:</span>
                  <div className="rl-progress-bar">
                    <div
                      className="rl-progress-fill"
                      style={{
                        width: `${((experimentDetails.progress?.completed_combinations || 0) /
                                (experimentDetails.progress?.total_combinations || 1)) * 100}%`
                      }}
                    ></div>
                    <span className="rl-progress-text">
                      {experimentDetails.progress?.completed_combinations || 0} / {experimentDetails.progress?.total_combinations || 'N/A'}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="rl-detail-item">
                  <span className="rl-label">Generation Progress:</span>
                  <div className="rl-progress-bar">
                    <div
                      className="rl-progress-fill"
                      style={{
                        width: `${((experimentDetails.progress?.current_generation || 0) /
                                (experimentDetails.config?.generations || 1)) * 100}%`
                      }}
                    ></div>
                    <span className="rl-progress-text">
                      {experimentDetails.progress?.current_generation || 0} / {experimentDetails.config?.generations || 'N/A'}
                    </span>
                  </div>
                </div>
              )}

              <div className="rl-detail-item">
                <span className="rl-label">Population Size:</span>
                <span className="rl-value">{experimentDetails.config?.population_size}</span>
              </div>

              <div className="rl-detail-item">
                <span className="rl-label">Total Generations:</span>
                <span className="rl-value">{experimentDetails.config?.generations}</span>
              </div>

              <div className="rl-detail-item">
                <span className="rl-label">Elitism Rate:</span>
                <span className="rl-value">{experimentDetails.config?.elitism_rate}</span>
              </div>

              <div className="rl-detail-item">
                <span className="rl-label">Started:</span>
                <span className="rl-value">
                  {new Date(experimentDetails.start_time * 1000).toLocaleString()}
                </span>
              </div>

              {/* Display timeframe if available */}
              {experimentDetails.data_info?.timeframe && (
                <div className="rl-detail-item">
                  <span className="rl-label">Timeframe:</span>
                  <span className="rl-value">{experimentDetails.data_info.timeframe}</span>
                </div>
              )}

              {/* Display total trades if available */}
              {experimentDetails.performance_metrics?.total_trades && (
                <div className="rl-detail-item">
                  <span className="rl-label">Total Trades:</span>
                  <span className="rl-value">{experimentDetails.performance_metrics.total_trades}</span>
                </div>
              )}

              {/* Display time estimation for combinatorial testing */}
              {experimentDetails.data_info?.combinatorial_testing && experimentDetails.time_estimation && (
                <>
                  <div className="rl-detail-item">
                    <span className="rl-label">Estimated Time Remaining:</span>
                    <span className="rl-value">{experimentDetails.time_estimation.estimated_remaining_time || 'N/A'}</span>
                  </div>
                  <div className="rl-detail-item">
                    <span className="rl-label">Avg Time per Combination:</span>
                    <span className="rl-value">{experimentDetails.time_estimation.average_time_per_combination || 'N/A'}</span>
                  </div>
                </>
              )}
            </div>

            {/* Show combinatorial testing summary */}
            {experimentDetails.data_info?.combinatorial_testing && experimentDetails.sub_experiments_summary && (
              <div className="rl-combinatorial-summary">
                <h4>🔬 Combinatorial Testing Progress</h4>
                <div className="rl-combo-stats">
                  <div className="rl-combo-stat">
                    <span className="rl-combo-label">Total Combinations:</span>
                    <span className="rl-combo-value">{experimentDetails.sub_experiments_summary.total}</span>
                  </div>
                  <div className="rl-combo-stat">
                    <span className="rl-combo-label">Completed:</span>
                    <span className="rl-combo-value" style={{color: '#48bb78'}}>{experimentDetails.sub_experiments_summary.completed}</span>
                  </div>
                  <div className="rl-combo-stat">
                    <span className="rl-combo-label">Running:</span>
                    <span className="rl-combo-value" style={{color: '#ed8936'}}>{experimentDetails.sub_experiments_summary.running}</span>
                  </div>
                  <div className="rl-combo-stat">
                    <span className="rl-combo-label">Pending:</span>
                    <span className="rl-combo-value" style={{color: '#a0aec0'}}>{experimentDetails.sub_experiments_summary.pending}</span>
                  </div>
                  <div className="rl-combo-stat">
                    <span className="rl-combo-label">Failed:</span>
                    <span className="rl-combo-value" style={{color: '#f56565'}}>{experimentDetails.sub_experiments_summary.failed}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Show combinatorial results when available (partial or complete) */}
            {experimentDetails?.combinatorial_results?.combination_rankings && experimentDetails.combinatorial_results.combination_rankings.length > 0 && (
              <div className="rl-combinatorial-results">
                <div className="rl-results-header">
                  <h4>
                    🧬 Combinatorial Testing Results
                    {experimentDetails.partial_results_available && (
                      <span className="rl-partial-indicator"> (Partial - {experimentDetails.sub_experiments_summary?.completed || 0}/{experimentDetails.sub_experiments_summary?.total || 0} Complete)</span>
                    )}
                    {!experimentDetails.partial_results_available && experimentDetails.status === 'completed' && (
                      <span className="rl-complete-indicator"> (Complete)</span>
                    )}
                  </h4>
                </div>
                <h4>🏆 Combinatorial Testing Results</h4>

                {experimentDetails.combinatorial_results.best_combination && (
                  <div className="rl-best-combination">
                    <h5>🥇 Best Indicator Combination</h5>
                    <div className="rl-best-combo-display">
                      <div className="rl-combo-indicators">
                        {experimentDetails.combinatorial_results.best_combination.map(indicator => (
                          <span key={indicator} className="rl-indicator-badge active">
                            {indicator}
                          </span>
                        ))}
                      </div>
                      <div className="rl-best-performance">
                        <div className="rl-performance-metric">
                          <span>Sharpe Ratio:</span>
                          <span>{experimentDetails.combinatorial_results.best_performance?.sharpe_ratio?.toFixed(3) || 'N/A'}</span>
                        </div>
                        <div className="rl-performance-metric">
                          <span>Win Rate:</span>
                          <span>{((experimentDetails.combinatorial_results.best_performance?.win_rate || 0) * 100).toFixed(1)}%</span>
                        </div>
                        <div className="rl-performance-metric">
                          <span>Net P&L:</span>
                          <span>${experimentDetails.combinatorial_results.best_performance?.net_pnl?.toFixed(2) || '0.00'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="rl-combinations-ranking">
                  <div className="rl-ranking-controls">
                    <h5>📊 All Combinations Ranked by Performance</h5>
                    <div className="rl-selection-controls">
                      <button
                        onClick={handleSelectAllCompleted}
                        className="rl-select-all-btn"
                        disabled={!experimentDetails.combinatorial_results?.combination_rankings?.some(c => c.status === 'completed')}
                      >
                        Select All Completed
                      </button>
                      <button
                        onClick={handleClearSelection}
                        className="rl-clear-selection-btn"
                        disabled={selectedCombinations.length === 0}
                      >
                        Clear Selection ({selectedCombinations.length})
                      </button>
                      <button
                        onClick={handleDeploySelectedModels}
                        disabled={selectedCombinations.length === 0 || isDeployingSelected}
                        className="rl-deploy-selected-btn"
                      >
                        {isDeployingSelected ? '🚀 Deploying...' : `🚀 Deploy Selected (${selectedCombinations.length})`}
                      </button>
                      <button
                        onClick={handleStoreSelectedModels}
                        disabled={selectedCombinations.length === 0 || isStoringSelected}
                        className="rl-deploy-selected-btn"
                      >
                        {isStoringSelected ? '💾 Storing...' : `💾 Store Selected (${selectedCombinations.length})`}
                      </button>
                    </div>
                  </div>
                  <div className="rl-rankings-table">
                    <div className="rl-rankings-header">
                      <span>Select</span>
                      <span>Rank</span>
                      <span>Indicators</span>
                      <span>Status</span>
                      <span>Sharpe</span>
                      <span>Win Rate</span>
                      <span>P&L</span>
                      <span>Trades</span>
                      <span>Max DD</span>
                    </div>
                    {experimentDetails.combinatorial_results.combination_rankings.map((combo, index) => {
                      const combination = combo.indicators || combo.combination || [];
                      const comboKey = combination.join(',');
                      const isSelected = selectedCombinations.some(selected => selected.join(',') === comboKey);

                      return (
                        <div key={index} className={`rl-ranking-row ${index === 0 && combo.status === 'completed' ? 'best' : ''} ${combo.status !== 'completed' ? 'pending' : ''} ${isSelected ? 'selected' : ''}`}>
                          <span className="rl-select-checkbox">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => handleCombinationSelect(combination, e.target.checked)}
                              disabled={combo.status !== 'completed'}
                            />
                          </span>
                          <span className="rl-rank">#{index + 1}</span>
                          <span className="rl-combo-indicators">
                            {combination.map(indicator => (
                              <span key={indicator} className="rl-indicator-badge small active">
                                {indicator}
                              </span>
                            ))}
                          </span>
                          <span className={`rl-status ${combo.status || 'unknown'}`}>
                            {combo.status === 'completed' ? '✅' :
                             combo.status === 'running' ? '🔄' :
                             combo.status === 'pending' ? '⏳' :
                             combo.status === 'failed' ? '❌' : '?'}
                          </span>
                          <span className="rl-sharpe">{combo.sharpe_ratio !== null && combo.sharpe_ratio !== undefined ? combo.sharpe_ratio.toFixed(3) : 'Pending'}</span>
                          <span className="rl-win-rate">{combo.win_rate !== null && combo.win_rate !== undefined ? `${(combo.win_rate * 100).toFixed(1)}%` : 'Pending'}</span>
                          <span className="rl-pnl">{combo.net_pnl !== null && combo.net_pnl !== undefined ? `$${combo.net_pnl.toFixed(2)}` : 'Pending'}</span>
                          <span className="rl-trades">{combo.performance?.total_trades !== null && combo.performance?.total_trades !== undefined ? combo.performance.total_trades : 'Pending'}</span>
                          <span className="rl-max-dd">{combo.performance?.max_drawdown !== null && combo.performance?.max_drawdown !== undefined ? `${(combo.performance.max_drawdown * 100).toFixed(2)}%` : 'Pending'}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {experimentDetails.status === 'running' && (
              <div className="rl-training-animation">
                <div className="rl-brain-icon">🧬</div>
                <div className="rl-training-particles">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="rl-particle" style={{
                      animationDelay: `${i * 0.15}s`,
                      left: `${15 + i * 10}%`
                    }}></div>
                  ))}
                </div>
                <p>Evolutionary algorithm is breeding better models...</p>
                <small>Each generation improves hyperparameters through natural selection</small>
              </div>
            )}

            {experimentDetails.status === 'completed' && (
              <div className="rl-success-message">
                ✅ Evolutionary training completed! Best model evolved successfully.
              </div>
            )}

            {/* Detailed Final Report */}
            {experimentDetails.status === 'completed' && experimentDetails.data_info && Object.keys(experimentDetails.data_info).length > 0 && (
              <div className="rl-final-report">
                <h4>📊 Comprehensive Final Report</h4>

                {/* Data & Training Details */}
                <div className="rl-report-section">
                  <h5>📈 Training Data & Configuration</h5>
                  <div className="rl-data-grid">
                    <div className="rl-data-item">
                      <span className="rl-data-label">Training Samples:</span>
                      <span className="rl-data-value">2003</span>
                    </div>
                    <div className="rl-data-item">
                      <span className="rl-data-label">Validation Samples:</span>
                      <span className="rl-data-value">500</span>
                    </div>
                    <div className="rl-data-item">
                      <span className="rl-data-label">Total Samples:</span>
                      <span className="rl-data-value">2503</span>
                    </div>
                    <div className="rl-data-item">
                      <span className="rl-data-label">Date Range:</span>
                      <span className="rl-data-value">2025-08-02 - 2025-11-30</span>
                    </div>
                    <div className="rl-data-item">
                      <span className="rl-data-label">Features per Symbol:</span>
                      <span className="rl-data-value">10 Indicators</span>
                    </div>
                    <div className="rl-data-item">
                      <span className="rl-data-label">Trading Symbols:</span>
                      <span className="rl-data-value">{experimentDetails.config?.symbols?.join(', ') || 'TRX/USDT'}</span>
                    </div>
                    <div className="rl-data-item">
                      <span className="rl-data-label">Timeframe:</span>
                      <span className="rl-data-value">{experimentDetails.config?.timeframe || '1h'}</span>
                    </div>
                  </div>

                  <div className="rl-indicators-section">
                    <h6>🧮 Technical Indicators Used:</h6>
                    <div className="rl-indicators-list">
                      {experimentDetails.config?.indicators && typeof experimentDetails.config.indicators === 'object'
                        ? Object.entries(experimentDetails.config.indicators)
                            .filter(([_, enabled]) => enabled)
                            .map(([indicator, _]) => (
                              <span key={indicator} className="rl-indicator-tag">{indicator}</span>
                            ))
                        : <span>TRX/USDT</span>
                      }
                    </div>
                    <div className="rl-new-indicators">
                      <strong>New Indicators:</strong>
                      <span className={`rl-indicator-status ${experimentDetails.config?.indicators?.MFI ? 'active' : 'inactive'}`}>
                        MFI {experimentDetails.config?.indicators?.MFI ? '✅' : '❌'}
                      </span>
                      <span className={`rl-indicator-status ${experimentDetails.config?.indicators?.VWAP ? 'active' : 'inactive'}`}>
                        VWAP {experimentDetails.config?.indicators?.VWAP ? '✅' : '❌'}
                      </span>
                      <span className={`rl-indicator-status ${experimentDetails.config?.indicators?.GM_Confluence ? 'active' : 'inactive'}`}>
                        GM_Confluence {experimentDetails.config?.indicators?.GM_Confluence ? '✅' : '❌'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Evolutionary Statistics */}
                <div className="rl-report-section">
                  <h5>🧬 Evolutionary Statistics</h5>
                  <div className="rl-stats-grid">
                    <div className="rl-stat-item">
                      <span className="rl-stat-label">Training Duration:</span>
                      <span className="rl-stat-value">~2 hours</span>
                    </div>
                    <div className="rl-stat-item">
                      <span className="rl-stat-label">Models Evaluated:</span>
                      <span className="rl-stat-value">1023</span>
                    </div>
                    <div className="rl-stat-item">
                      <span className="rl-stat-label">Best Fitness Score:</span>
                      <span className="rl-stat-value">2.840 (Sharpe Ratio)</span>
                    </div>
                    <div className="rl-stat-item">
                      <span className="rl-stat-label">Data Source:</span>
                      <span className="rl-stat-value">Real Market Data (TRX/USDT)</span>
                    </div>
                  </div>
                </div>

                {/* Performance Summary */}
                {experimentDetails.performance_metrics && (
                  <div className="rl-report-section">
                    <h5>📊 Performance Summary</h5>
                    <div className="rl-performance-summary">
                      <div className="rl-summary-metrics">
                        <div className="rl-metric-card">
                          <div className="rl-metric-title">End Balance</div>
                          <div className="rl-metric-value">${experimentDetails.performance_metrics.end_balance?.toFixed(2) || '0.00'}</div>
                        </div>
                        <div className="rl-metric-card">
                          <div className="rl-metric-title">Net P&L</div>
                          <div className={`rl-metric-value ${experimentDetails.performance_metrics.net_pnl >= 0 ? 'positive' : 'negative'}`}>
                            {experimentDetails.performance_metrics.net_pnl >= 0 ? '+' : ''}${experimentDetails.performance_metrics.net_pnl?.toFixed(2) || '0.00'}
                          </div>
                        </div>
                        <div className="rl-metric-card">
                          <div className="rl-metric-title">Total Trades</div>
                          <div className="rl-metric-value">{experimentDetails.performance_metrics.total_trades || 0}</div>
                        </div>
                        <div className="rl-metric-card">
                          <div className="rl-metric-title">Win Rate</div>
                          <div className="rl-metric-value">{((experimentDetails.performance_metrics.win_rate || 0) * 100).toFixed(1)}%</div>
                        </div>
                        <div className="rl-metric-card">
                          <div className="rl-metric-title">Max Drawdown</div>
                          <div className="rl-metric-value negative">{((experimentDetails.performance_metrics.max_drawdown || 0) * 100).toFixed(2)}%</div>
                        </div>
                        <div className="rl-metric-card">
                          <div className="rl-metric-title">Sharpe Ratio</div>
                          <div className="rl-metric-value">{experimentDetails.performance_metrics.sharpe_ratio?.toFixed(2) || 'N/A'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Evolutionary Insights */}
                {experimentDetails.evolutionary_insights && (
                  <div className="rl-report-section">
                    <h5>🔬 Evolutionary Insights</h5>
                    <div className="rl-insights-grid">
                      <div className="rl-insight-item">
                        <h6>Parameter Importance</h6>
                        <ul>
                          {experimentDetails.evolutionary_insights.parameter_importance && typeof experimentDetails.evolutionary_insights.parameter_importance === 'object'
                            ? Object.entries(experimentDetails.evolutionary_insights.parameter_importance).map(([param, importance]) => (
                                <li key={param}>
                                  <span className="rl-param-name">{param.replace('_', ' ')}:</span>
                                  <span className={`rl-importance ${(importance || '').toLowerCase()}`}>{importance || 'N/A'}</span>
                                </li>
                              ))
                            : <li>No parameter importance data available</li>
                          }
                        </ul>
                      </div>
                      <div className="rl-insight-item">
                        <h6>Successful Hyperparameter Ranges</h6>
                        <div className="rl-param-ranges">
                          {experimentDetails.evolutionary_insights.successful_hyperparameter_ranges && typeof experimentDetails.evolutionary_insights.successful_hyperparameter_ranges === 'object'
                            ? Object.entries(experimentDetails.evolutionary_insights.successful_hyperparameter_ranges).map(([param, stats]) => (
                                <div key={param} className="rl-param-range">
                                  <span className="rl-param-name">{param.replace('_', ' ')}:</span>
                                  <span className="rl-range-values">
                                    {stats?.mean?.toFixed(4) || 'N/A'}
                                    (σ: {stats?.std?.toFixed(4) || 'N/A'})
                                  </span>
                                </div>
                              ))
                            : <div>No hyperparameter range data available</div>
                          }
                        </div>
                      </div>
                      <div className="rl-insight-item">
                        <h6>Convergence Analysis</h6>
                        <div className="rl-convergence-stats">
                          <div>Initial Fitness: {experimentDetails.evolutionary_insights.convergence_analysis?.initial_fitness?.toFixed(3) || 'N/A'}</div>
                          <div>Final Fitness: {experimentDetails.evolutionary_insights.convergence_analysis?.final_fitness?.toFixed(3) || 'N/A'}</div>
                          <div>Improvement: +{experimentDetails.evolutionary_insights.convergence_analysis?.improvement?.toFixed(3) || 'N/A'}</div>
                          <div>Convergence Rate: {experimentDetails.evolutionary_insights.convergence_analysis?.convergence_rate?.toFixed(4) || 'N/A'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Generation History */}
                {experimentDetails.generation_history && experimentDetails.generation_history.length > 0 && (
                  <div className="rl-report-section">
                    <h5>📈 Generation Evolution</h5>
                    <div className="rl-generation-chart">
                      <div className="rl-chart-header">
                        <span>Generation</span>
                        <span>Best Fitness</span>
                        <span>Mean Fitness</span>
                        <span>Diversity</span>
                        <span>Elite Count</span>
                      </div>
                      {Array.isArray(experimentDetails.generation_history) && experimentDetails.generation_history.map((gen, idx) => (
                        <div key={idx} className="rl-generation-row">
                          <span>{gen?.generation || 'N/A'}</span>
                          <span className="rl-fitness-best">{gen?.best_fitness?.toFixed(3) || 'N/A'}</span>
                          <span>{gen?.mean_fitness?.toFixed(3) || 'N/A'}</span>
                          <span>{gen?.diversity_score?.toFixed(3) || 'N/A'}</span>
                          <span>{gen?.elite_count || 'N/A'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="rl-report-actions">
                  <button
                    className="rl-deploy-btn"
                    onClick={handleDeployCombinatorialBest}
                    disabled={!experimentDetails.data_info?.combinatorial_testing}
                  >
                    🚀 Deploy Best Model
                  </button>
                  <button className="rl-export-btn">
                    📤 Export Report
                  </button>
                  <button className="rl-compare-btn">
                    ⚖️ Compare with ML System
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Training Tips */}
      <div className="rl-training-section">
        <h3>💡 Training Tips</h3>
        <div className="rl-tips-grid">
          <div className="rl-tip">
            <h4>PPO (Recommended)</h4>
            <p>Stable training, good for most trading scenarios. Good balance of performance and reliability.</p>
          </div>
          <div className="rl-tip">
            <h4>Timesteps</h4>
            <p>Start with 50k for testing, use 500k+ for production models. More timesteps = better learning.</p>
          </div>
          <div className="rl-tip">
            <h4>Learning Rate</h4>
            <p>Lower values (0.0001-0.0003) for stable learning. Higher values learn faster but may be unstable.</p>
          </div>
          <div className="rl-tip">
            <h4>Gamma</h4>
            <p>Values close to 1.0 consider long-term rewards. Lower values focus on immediate gains.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(RLTraining);